import styled from 'styled-components';
import { Modal, Radio } from 'antd';
import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import FilterIcon from '@/assets/mo/Filter.svg?react';
import ArrowDownIcon from '@/assets/pc/Arrow-Down.svg?react';

interface FilterBarProps {
  selectedFilters: {
    보험종류: string[];
    사고유형: string[];
  };
  onChange: (filters: { 보험종류: string[]; 사고유형: string[] }) => void;
  sort: 'recent' | 'view';
  onChangeSort: (value: 'recent' | 'view') => void;
}

const insuranceOptions = ['전체', '실손보험', '자동차보험', '배상책임', '진단금/기타'];
const issueOptions = [
  '전체',
  '교통사고',
  '상해사고',
  '후유장해',
  '진단금',
  '누수',
  '도수/체외충격파',
  '백내장',
  '고지의무',
  '전립선',
  '맘모톰',
  '하이푸',
  '비밸브/비중격',
  '무릎치료',
  '어린이',
  '기타',
];

interface FilterBarProps {
  selectedFilters: {
    보험종류: string[];
    사고유형: string[];
  };
  onChange: (filters: { 보험종류: string[]; 사고유형: string[] }) => void;
  sort: 'recent' | 'view';
  onChangeSort: (value: 'recent' | 'view') => void;
}

const FilterBar = ({ selectedFilters, onChange, sort, onChangeSort }: FilterBarProps) => {
  const [typeModalOpen, setTypeModalOpen] = useState(false);
  const [issueModalOpen, setIssueModalOpen] = useState(false);
  const [selectedType, setSelectedType] = useState<string[]>(selectedFilters.보험종류);
  const [selectedIssue, setSelectedIssue] = useState<string[]>(selectedFilters.사고유형);

  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const typeFromUrl = searchParams.get('type');
    const issueFromUrl = searchParams.get('issue');
    if (typeFromUrl) setSelectedType([typeFromUrl]);
    if (issueFromUrl) setSelectedIssue([issueFromUrl]);
  }, []);

  const handleConfirm = () => {
    const filters = {
      보험종류: selectedType.length > 0 ? selectedType : ['전체'],
      사고유형: selectedIssue.length > 0 ? selectedIssue : ['전체'],
    };
    onChange(filters);
    setSearchParams({ type: filters.보험종류[0], issue: filters.사고유형[0] });
    setTypeModalOpen(false);
    setIssueModalOpen(false);
  };

  return (
    <Container>
      <LeftGroup>
        <BlackButton>
          <IconWrapper>
            <FilterIcon />
          </IconWrapper>
          <BlackButtonText>필터</BlackButtonText>
        </BlackButton>

        <WhiteButton onClick={() => setTypeModalOpen(true)}>
          <WhiteButtonText>{selectedType[0] || '보험 종류를 선택해보세요'}</WhiteButtonText>
          <ArrowDownIcon />
        </WhiteButton>

        <Modal
          title="보험 종류 선택"
          open={typeModalOpen}
          footer={null}
          onCancel={() => setTypeModalOpen(false)}
          centered
        >
          <CustomDropdownContent
            options={insuranceOptions}
            selected={selectedType[0]}
            setSelected={(v) => {
              setSelectedType([v]);
              setSelectedIssue(['전체']); // ✅ 보험 종류 고르면 사고유형 초기화
            }}
            onConfirm={handleConfirm}
            onCancel={() => setTypeModalOpen(false)}
          />
        </Modal>

        <WhiteButton onClick={() => setIssueModalOpen(true)}>
          <WhiteButtonText>{selectedIssue[0] || '사고 유형을 선택해보세요'}</WhiteButtonText>
          <ArrowDownIcon />
        </WhiteButton>

        <Modal
          title="사고 유형 선택"
          open={issueModalOpen}
          footer={null}
          onCancel={() => setIssueModalOpen(false)}
          centered
        >
          <CustomDropdownContent
            options={issueOptions}
            selected={selectedIssue[0]}
            setSelected={(v) => {
              setSelectedIssue([v]);
              setSelectedType(['전체']);
            }}
            onConfirm={handleConfirm}
            onCancel={() => setIssueModalOpen(false)}
          />
        </Modal>
      </LeftGroup>

      <RightGroup>
        <SortButton onClick={() => onChangeSort('recent')}>
          <SortText selected={sort === 'recent'}>최신순</SortText>
        </SortButton>
        <SortButton onClick={() => onChangeSort('view')}>
          <SortText selected={sort === 'view'}>조회순</SortText>
        </SortButton>
      </RightGroup>
    </Container>
  );
};

export default FilterBar;

// ---------------------- CustomDropdownContent ----------------------

interface CustomDropdownProps {
  options: string[];
  selected: string;
  setSelected: (value: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
}

const CustomDropdownContent = ({
  options,
  selected,
  setSelected,
  onConfirm,
  onCancel,
}: CustomDropdownProps) => {
  return (
    <DropdownWrapper>
      <MiddleSection>
        <Radio.Group value={selected} onChange={(e) => setSelected(e.target.value)}>
          {options.map((option) => (
            <Radio key={option} value={option} style={{ display: 'block', marginBottom: 8 }}>
              {option}
            </Radio>
          ))}
        </Radio.Group>
      </MiddleSection>

      <BottomSection>
        <CancelButton onClick={onCancel}>
          <CancelText>취소</CancelText>
        </CancelButton>
        <ConfirmButton onClick={onConfirm}>
          <ConfirmText>확인</ConfirmText>
        </ConfirmButton>
      </BottomSection>
    </DropdownWrapper>
  );
};
// -------- styled components --------

const Container = styled.div`
  display: flex;
  max-width: 1240px;
  width: 100%;
  padding: 31px 20px;
  justify-content: space-between;
  align-items: center;
  margin: 0 auto;
  flex-wrap: wrap;
  gap: 16px;

  @media (max-width: 1024px) {
    flex-direction: column;
    align-items: flex-start;
    padding: 24px 16px;
  }
`;

const LeftGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;

  @media (max-width: 1024px) {
    width: 100%;
  }
`;

const RightGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 1024px) {
    width: 100%;
    justify-content: flex-start;
  }
`;

const BlackButton = styled.div`
  display: flex;
  padding: 4px 20px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  border-radius: 99px;
  border: 1px solid #d6dae2;
  background: #3a3a3a;

  @media (max-width: 768px) {
    padding: 4px 16px;
  }
`;

const IconWrapper = styled.div`
  display: flex;
  width: 18px;
  height: 18px;
  justify-content: center;
  align-items: center;
`;

const BlackButtonText = styled.span`
  color: #fff;
  font-family: 'Spoqa Han Sans Neo';
  font-size: 14px;
  font-weight: 400;
  line-height: 120%;
  letter-spacing: -0.28px;
`;

const WhiteButton = styled(BlackButton)`
  background: #fff;
  cursor: pointer;
`;

const WhiteButtonText = styled(BlackButtonText)`
  color: #3a3a3a;
`;

const SortButton = styled.div`
  display: flex;
  height: 48px;
  padding: 10px 16px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  cursor: pointer;
`;

const SortText = styled.div<{ selected?: boolean }>`
  color: ${({ selected }) => (selected ? '#000C24' : '#6F6F6F')};
  font-family: 'Spoqa Han Sans Neo';
  font-size: 14px;
  font-weight: ${({ selected }) => (selected ? 700 : 500)};
  line-height: 120%;
  letter-spacing: -0.28px;
`;

const DropdownWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 10px 16px;
  gap: 8px;
`;

const MiddleSection = styled.div`
  display: flex;
  padding-top: 8px;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
`;

const BottomSection = styled.div`
  display: flex;
  padding-top: 16px;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  width: 100%;
`;

const CancelButton = styled.div`
  display: flex;
  height: 32px;
  padding: 8px 16px;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
`;

const CancelText = styled.span`
  color: #777;
  font-family: 'Spoqa Han Sans Neo';
  font-size: 14px;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: -0.28px;
`;

const ConfirmButton = styled.div`
  display: flex;
  height: 32px;
  padding: 8px 16px;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  background: #0b46dd;
  cursor: pointer;
`;

const ConfirmText = styled.span`
  color: #fff;
  font-family: 'Spoqa Han Sans Neo';
  font-size: 14px;
  font-weight: 400;
  line-height: 150%;
  letter-spacing: -0.28px;
`;
